// driveApi.js - Frontend utility for Google Drive API calls
const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || 'http://localhost:5001';

/**
 * Get Firebase auth token from current user
 */
async function getAuthToken() {
    const { auth } = await import('../firebase/firebaseConfig');
    const user = auth.currentUser;
    if (!user) {
        throw new Error('User not authenticated');
    }
    return user.getIdToken();
}

let cachedDriveStatus = null;

/**
 * Handle response and check for auth errors
 */
async function handleResponse(response) {
    if (!response.ok) {
        const error = await response.json();

        // If server says reconnect is required (token expired/revoked),
        // clear local cache so UI updates
        if (response.status === 401 && error.reconnectRequired) {
            console.warn('⚠️ Drive connection expired/revoked. Clearing local cache.');
            cachedDriveStatus = { linked: false, adminUid: null };
        }

        throw new Error(error.message || 'Drive operation failed');
    }
    return response.json();
}

/**
 * Check if Google Drive is linked for the current user's admin
 * @param {boolean} forceRefresh - Whether to bypass the cache
 * @returns {Promise<{linked: boolean, adminUid: string}>}
 */
export async function checkDriveStatus(forceRefresh = false) {
    if (!forceRefresh && cachedDriveStatus) {
        return cachedDriveStatus;
    }

    try {
        const token = await getAuthToken();
        const response = await fetch(`${BACKEND_URL}/api/drive/status`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            console.warn('Drive status check failed:', response.status);
            return { linked: false, adminUid: null };
        }

        const data = await response.json();
        cachedDriveStatus = {
            linked: data.linked || false,
            adminUid: data.adminUid || null
        };
        return cachedDriveStatus;
    } catch (error) {
        console.error('Error checking Drive status:', error);
        return { linked: false, adminUid: null };
    }
}

/**
 * Get OAuth authorization URL for linking Google Drive (Admin only)
 * @returns {Promise<string>} Authorization URL
 */
export async function getDriveAuthUrl(platform = 'desktop', source = '') {
    const token = await getAuthToken();
    let url = `${BACKEND_URL}/api/drive/auth-url?platform=${platform}`;
    if (source) url += `&source=${source}`;
    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to get auth URL');
    }

    const data = await response.json();
    return data.authUrl;
}

/**
 * Upload a PDF file to Google Drive
 * @param {File} file - PDF file to upload
 * @param {string} branchName - Branch/office name for folder structure
 * @param {string} clientName - Client name for folder structure
 * @param {string} policyNumber - Policy number for filename
 * @returns {Promise<{fileId: string, webViewLink: string}>}
 */
export async function uploadToDrive(file, branchName, clientName, policyNumber, subFolder = 'Policies', customFileName) {
    const token = await getAuthToken();

    const formData = new FormData();
    formData.append('pdf', file);
    formData.append('branchName', branchName);
    formData.append('clientName', clientName);
    if (policyNumber) formData.append('policyNumber', policyNumber);
    formData.append('subFolder', subFolder);
    if (customFileName) formData.append('fileName', customFileName);

    const response = await fetch(`${BACKEND_URL}/api/drive/upload`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`
            // Don't set Content-Type for FormData, let browser set it with boundary
        },
        body: formData
    });

    return handleResponse(response);
}

/**
 * Get view URL for a Drive file
 * @param {string} fileId - Google Drive file ID
 * @returns {Promise<string>} View URL
 */
export async function getDriveViewUrl(fileId) {
    const token = await getAuthToken();

    const response = await fetch(`${BACKEND_URL}/api/drive/view-url`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({ fileId })
    });

    const data = await handleResponse(response);
    return data.viewUrl;
}

/**
 * Check existence of multiple Drive files
 * @param {string[]} fileIds - Array of Google Drive file IDs
 * @returns {Promise<Object>} Map of fileId to boolean existence status
 */
export async function checkFilesExistence(fileIds) {
    if (!fileIds || fileIds.length === 0) return {};

    const token = await getAuthToken();

    const response = await fetch(`${BACKEND_URL}/api/drive/check-existence`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({ fileIds })
    });

    const data = await handleResponse(response);
    return data.existenceMap;
}

/**
 * Create a shareable link for a Drive file
 * @param {string} fileId - Google Drive file ID
 * @returns {Promise<{shareableLink: string, viewLink: string, downloadLink: string}>}
 */
export async function createDriveShareLink(fileId) {
    const token = await getAuthToken();

    const response = await fetch(`${BACKEND_URL}/api/drive/share-url`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({ fileId })
    });

    return handleResponse(response);
}

/**
 * Move a Drive file to trash (Admin only)
 * @param {string} fileId - Google Drive file ID
 * @returns {Promise<void>}
 */
export async function trashDriveFile(fileId) {
    const token = await getAuthToken();

    const response = await fetch(`${BACKEND_URL}/api/drive/trash`, {
        method: 'DELETE',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({ fileId })
    });

    await handleResponse(response);
}

/**
 * Unlink Google Drive (Admin only)
 * @returns {Promise<void>}
 */
export async function unlinkDrive() {
    const token = await getAuthToken();

    const response = await fetch(`${BACKEND_URL}/api/drive/unlink`, {
        method: 'DELETE',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
    });

    await handleResponse(response);
    cachedDriveStatus = null;
}
